/**
 * Usage Analytics Dashboard
 *
 * Platform-wide feature adoption, completion funnels,
 * drop-off analysis, channel performance, and struggle indicators.
 */
import { useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart3,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Activity,
  MessageSquare,
  Layers,
} from 'lucide-react';
import { Button } from '@respectly/ui';
import { useUsageStore } from '../../stores/useUsageStore';
import type { UsagePeriod } from '@respectly/shared-types';
import {
  UsageMetricCard,
  FeatureAdoptionChart,
  UsageTrendChart,
  CompletionFunnelCard,
  DropOffTable,
  SmsChannelCard,
  EmailChannelCard,
  VoiceChannelCard,
  StruggleIndicatorsPanel,
  TopAccountsTable,
} from '../../components/usage';

// ── Animation variants ────────────────────────────────────────────────────
const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.06, delayChildren: 0.05 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } },
};

// ── Period tabs ───────────────────────────────────────────────────────────
const PERIODS: { value: UsagePeriod; label: string }[] = [
  { value: '7d', label: '7D' },
  { value: '30d', label: '30D' },
  { value: '90d', label: '90D' },
];

const FUNNEL_CONFIG = [
  { key: 'campaigns' as const, label: 'Campaigns', color: '#3b82f6' },
  { key: 'documents' as const, label: 'Documents', color: '#8b5cf6' },
  { key: 'forms' as const, label: 'Forms', color: '#10b981' },
  { key: 'voice' as const, label: 'Voice Calls', color: '#f59e0b' },
  { key: 'workflows' as const, label: 'Workflows', color: '#a855f7' },
];

// ── Page component ────────────────────────────────────────────────────────
export function UsageDashboard() {
  const { data, isLoading, error, period, lastUpdatedAt, loadDashboard, setPeriod, clearError } =
    useUsageStore();

  useEffect(() => {
    loadDashboard();
  }, []);

  const handleRefresh = useCallback(() => {
    loadDashboard();
  }, [loadDashboard]);

  const updatedLabel = useMemo(() => {
    if (!lastUpdatedAt) return null;
    const diffMs = Date.now() - lastUpdatedAt.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Updated just now';
    if (diffMin === 1) return 'Updated 1 min ago';
    return `Updated ${diffMin} min ago`;
  }, [lastUpdatedAt]);

  return (
    <div className="space-y-6">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center shadow-sm">
            <BarChart3 className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">Usage Analytics</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Platform-wide feature adoption, completion rates & drop-off analysis
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Updated label */}
          {updatedLabel && (
            <span className="text-xs text-slate-400 dark:text-slate-500 px-2">{updatedLabel}</span>
          )}

          {/* Period selector */}
          <div className="flex items-center gap-0.5 bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                  period === p.value
                    ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Refresh */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isLoading}
            className="gap-1.5"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* ── Error banner ───────────────────────────────────────────────── */}
      {error && (
        <div className="flex items-center justify-between gap-3 px-4 py-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
          <span>{error.message}</span>
          <button onClick={clearError} className="text-red-400 hover:text-red-600 text-lg leading-none">×</button>
        </div>
      )}

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="show"
        className="space-y-6"
      >
        {/* ── Section 1: KPI Summary Strip ───────────────────────────── */}
        <motion.div variants={itemVariants}>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <UsageMetricCard
              label="Features Active"
              value={data?.summary.totalFeatureActivations ?? '—'}
              subLabel="distinct feature types"
              icon={Layers}
              iconColor="text-blue-500"
              iconBg="bg-blue-500/10 dark:bg-blue-500/20"
              isLoading={isLoading && !data}
            />
            <UsageMetricCard
              label="Completion Rate"
              value={data ? `${data.summary.overallCompletionRate}%` : '—'}
              subLabel="of started processes"
              icon={CheckCircle2}
              iconColor="text-emerald-500"
              iconBg="bg-emerald-500/10 dark:bg-emerald-500/20"
              isLoading={isLoading && !data}
            />
            <UsageMetricCard
              label="Total Drop-offs"
              value={data?.summary.totalDropOffs.toLocaleString() ?? '—'}
              subLabel="abandoned processes"
              icon={XCircle}
              iconColor="text-red-500"
              iconBg="bg-red-500/10 dark:bg-red-500/20"
              isLoading={isLoading && !data}
            />
            <UsageMetricCard
              label="Active Workflows"
              value={data?.summary.activeWorkflows.toLocaleString() ?? '—'}
              subLabel="enrollments running"
              icon={Activity}
              iconColor="text-violet-500"
              iconBg="bg-violet-500/10 dark:bg-violet-500/20"
              isLoading={isLoading && !data}
            />
            <UsageMetricCard
              label="Messages Sent"
              value={data?.summary.totalMessagesSent.toLocaleString() ?? '—'}
              subLabel="SMS + email this period"
              icon={MessageSquare}
              iconColor="text-cyan-500"
              iconBg="bg-cyan-500/10 dark:bg-cyan-500/20"
              isLoading={isLoading && !data}
            />
            <UsageMetricCard
              label="Active Accounts"
              value={data?.summary.strugglingAccounts.toLocaleString() ?? '—'}
              subLabel="with activity this period"
              icon={BarChart3}
              iconColor="text-amber-500"
              iconBg="bg-amber-500/10 dark:bg-amber-500/20"
              isLoading={isLoading && !data}
            />
          </div>
        </motion.div>

        {/* ── Section 2: Feature Adoption + Trend ───────────────────── */}
        <motion.div variants={itemVariants}>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            <div className="lg:col-span-3">
              <FeatureAdoptionChart
                data={data?.featureAdoption ?? []}
                isLoading={isLoading && !data}
              />
            </div>
            <div className="lg:col-span-2">
              <UsageTrendChart
                data={data?.usageTrend ?? []}
                isLoading={isLoading && !data}
              />
            </div>
          </div>
        </motion.div>

        {/* ── Section 3: Completion Funnels ──────────────────────────── */}
        <motion.div variants={itemVariants}>
          <div className="mb-3">
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
              Completion Funnels
              <span className="text-xs text-slate-400 font-normal">— where users drop off</span>
            </h2>
          </div>
          <div className="flex gap-4 flex-wrap">
            {FUNNEL_CONFIG.map((f) => (
              <CompletionFunnelCard
                key={f.key}
                featureName={f.label}
                stages={data?.funnels[f.key] ?? []}
                color={f.color}
                isLoading={isLoading && !data}
              />
            ))}
          </div>
        </motion.div>

        {/* ── Section 4: Drop-off Table ──────────────────────────────── */}
        <motion.div variants={itemVariants}>
          <DropOffTable
            items={data?.dropOffs ?? []}
            isLoading={isLoading && !data}
          />
        </motion.div>

        {/* ── Section 5: Channel Performance ─────────────────────────── */}
        <motion.div variants={itemVariants}>
          <div className="mb-3">
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
              Channel Performance
              <span className="text-xs text-slate-400 font-normal">— how messages are being delivered</span>
            </h2>
          </div>
          <div className="flex gap-4 flex-wrap">
            <SmsChannelCard
              data={data?.channelPerformance.sms ?? { sent: 0, delivered: 0, failed: 0, deliveryRate: 0, failureRate: 0 }}
              isLoading={isLoading && !data}
            />
            <EmailChannelCard
              data={data?.channelPerformance.email ?? { sent: 0, delivered: 0, failed: 0, opened: 0, clicked: 0, deliveryRate: 0, failureRate: 0, openRate: 0, clickRate: 0 }}
              isLoading={isLoading && !data}
            />
            <VoiceChannelCard
              data={data?.channelPerformance.voice ?? { initiated: 0, completed: 0, failed: 0, completionRate: 0, avgDurationSeconds: 0, consentCaptured: 0, consentCaptureRate: 0 }}
              isLoading={isLoading && !data}
            />
          </div>
        </motion.div>

        {/* ── Section 6: Struggle Indicators + Top Accounts ─────────── */}
        <motion.div variants={itemVariants}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <StruggleIndicatorsPanel
              data={data?.struggleIndicators ?? []}
              isLoading={isLoading && !data}
            />
            <TopAccountsTable
              accounts={data?.topAccounts ?? []}
              isLoading={isLoading && !data}
            />
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}

export default UsageDashboard;
